import { useCallback } from 'react';
import useElement from './useElement';

type PositionType = 'default' | 'tab' | 'drawer' | 'drawer-lg' | 'drawer-sm';
type PositionInput = PositionType | number;

const POSITION_VALUES = {
  default: { x: 10, y: 10 },
  tab: { x: 450, y: 10 },
  drawer: { x: 580, y: 10 },
  'drawer-sm': { x: 460, y: 10 },
  'drawer-lg': { x: 740, y: 10 },
};

const setElementPosition = (el: HTMLElement | null, position: PositionInput, isFooterVisible: boolean) => {
  if (!el) return;

  let x: number;
  let y = 10;
  let isDrawer = false;

  if (typeof position === 'number') {
    x = position;
  } else {
    ({ x, y } = POSITION_VALUES[position]);
    isDrawer = position === 'drawer' || position === 'drawer-lg' || position === 'drawer-sm';
  }

  const yValue = isFooterVisible && !isDrawer ? 76 : y;

  el.style.transition = 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
  el.style.setProperty('right', `${x}px`, 'important');
  el.style.setProperty('bottom', `${yValue}px`, 'important');
};

const useChatWidget = () => {
  const { element: hubspotElement } = useElement('#hubspot-messages-iframe-container');
  const { element: blipChatElement } = useElement('#blip-chat-container #blip-chat-open-iframe', { observe: true });
  const { isVisible: isFooterVisible } = useElement('#page-footer', { trackVisibility: true });

  const setPosition = useCallback(
    (position: PositionInput) => {
      setElementPosition(hubspotElement, position, isFooterVisible);
      setElementPosition(blipChatElement, position, isFooterVisible);
    },
    [hubspotElement, blipChatElement, isFooterVisible]
  );

  const hide = useCallback(
    (hideIt: boolean) => {
      if (hubspotElement) {
        hubspotElement.style.setProperty('display', hideIt ? 'none' : 'initial', 'important');
      }
      if (blipChatElement) {
        blipChatElement.style.setProperty('display', hideIt ? 'none' : 'initial', 'important');
      }
    },
    [hubspotElement, blipChatElement]
  );

  const openBlipChat = useCallback(() => {
    if (window.blipChatInstance?.toogleChat) {
      window.blipChatInstance.toogleChat();
      return;
    }

    if (window.blipChatBuilder?.toogleChat) {
      window.blipChatBuilder.toogleChat();
    }
  }, []);

  const sendBlipChatMessage = useCallback((message: string) => {
    if (window.blipChatInstance?.sendMessage) {
      window.blipChatInstance.sendMessage({
        type: 'text/plain',
        content: message,
      });
      return;
    }

    if (window.blipChatBuilder?.sendMessage) {
      window.blipChatBuilder.sendMessage({
        type: 'text/plain',
        content: message,
      });
    }
  }, []);

  return {
    setPosition,
    hide,
    openBlipChat,
    sendBlipChatMessage,
    hubspotElement,
    blipChatElement,
    isFooterVisible,
  };
};

export default useChatWidget;
